import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  CommentAction,
  Ticket,
  TicketAction,
  TicketStatus,
  useUpdateAssignedToMutation,
  useUpdateLinkedTicketMutation,
  useUpdateTicketCommentMutation,
  useUpdateTitleMutation,
} from "@/services/LiveQueueService";
import {
  useUpdateTicketStatusMutation,
  useLazyGetTickeyByIdQuery,
} from "@/services/LiveQueueService";
import { useWaitingList } from "../components/admin/serve-ticket/hooks/UseWaitingList";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { Comment } from "@/services/LiveQueueService";
import { Gender, UserData } from "@/app/shared_enums/enums";
import { useSaveUserAgainstTicketMutation } from "@/services/UserApi";
import { toast } from "@/components/ui/UseToast";
import { ErrorType } from "@/app/types";
import { useGenerateTicketForServiceMutation, useGenerateUserTicketForServiceMutation } from "@/services/SelfServiceKiosk";
import { service } from "@/mock/services";
import { useGetCounterByIdQuery } from "../services/CounterApi";
import { useGetDepartmentByIdQuery } from "../services/DepartmentApi";
import { ReRouteType } from "../app/types/department";
import { TicketResponse } from "@/services/SelfServiceKiosk";

interface TicketContextType {
  currentTicket: Ticket | null;
  comments: Comment[];
  nextTicket: () => void;
  holdTicket: () => void;
  marketAbsent: () => void;
  addComment: (ticketId: number, content: string) => Promise<void>;
  updateComment: (
    ticketId: number,
    commentId: number,
    content: string
  ) => Promise<void>;
  deleteComment: (ticketId: number, commentId: number) => Promise<void>;
  fetchComments: (ticketId: string) => Promise<void>;
  routeTicket: (counterId: number, serviceId: number) => Promise<{
    success: boolean;
    message: string;
  }>;
  selectTicket: (
    ticketNumber: string,
    service_id: number,
    r_ticket_id: number,
    title: string,
    userId?: number,
    description?: string
  ) => void;
  startServing: () => void;
  loading: boolean;
  error: string | null;
  userId: number | null;
  setUserId: (userId: number | null) => void; // Correctly typed setState function
  addUser: (userData: UserData) => Promise<{
    success: boolean;
    message: string;
  }>; // Add the new function to the context type
  addDetails: (data: { title: string; description?: string }) => Promise<{
    success: boolean;
    message: string;
  }>;
  handleTicketAction: (
    TicketAction: TicketAction,
    setSelectedAction: React.Dispatch<React.SetStateAction<TicketAction | null>>
  ) => void;
  isCounterOpen: boolean;
  setCounterOpen: (isCounterOpen: boolean) => void;
  updateLinkedTicket: (data: { ticketId: string }) => Promise<{
    success: boolean;
    message: string;
  }>;
  isAccrossDepartment: boolean;
  // Add this to the TicketContextType interface
  setAccrossDepartment: () => void;
  /**
   * Converts a R series ticket into a S series ticket.
   *
   * @returns {number} The ID of the newly created service ticket.
   */
  convertTicketFromRToS: (
    ticketNumber: string,
    service_id: number,
    r_ticket_id: number,
    title: string,
    userId?: number,
    description?: string,
    options?: { throwOnError?: boolean }
  ) => Promise<TicketResponse | undefined>;
}

const TicketContext = createContext<TicketContextType>({} as TicketContextType);

export function TicketProvider({ children }: { children: React.ReactNode }) {
  const today = new Date();
  const localDate = today.toLocaleDateString("en-CA"); //
  const [isAccrossDepartment, setIsAccrossDepartment] = useState(false);
  const [isCounterOpen, setIsCounterOpen] = useState(false);
  const [comment, setComments] = useState<Comment[]>([]);
  const [triggerGetTickeById] = useLazyGetTickeyByIdQuery();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [generatingTicket, setGeneratingTicket] = useState(false);
  const [currentTicket, setCurrentTicket] = useState<Ticket | null>(null);

  const [userId, setUserId] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  function readTicket(text: string) {
    const speech = new SpeechSynthesisUtterance();
    speech.text = text;
    speech.lang = "en-US";
    speech.volume = 1; // Adjust volume (0 to 1)
    speech.rate = 1.25; // Adjust speed (0.1 to 10)
    speech.pitch = 1; // Adjust pitch (0 to 2)

    window.speechSynthesis.speak(speech);
  }

  const [updateStatus] = useUpdateTicketStatusMutation();
  const [updateAssignedTo] = useUpdateAssignedToMutation();
  const { waitingList, refetchWaitingList } = useWaitingList();

  const [updateTicket] = useUpdateTicketCommentMutation();
  const [addUserAgainstTicket] = useSaveUserAgainstTicketMutation();
  const [generateTicket] = useGenerateUserTicketForServiceMutation();
  const [updateTitle] = useUpdateTitleMutation();
  const [addLinkedTicket] = useUpdateLinkedTicketMutation();
  const staffId = String(userInfo?.id ?? 0);

  function setCounterOpen(isCounterOpen: boolean) {
    localStorage.setItem("counter_open", isCounterOpen.toString());
    setIsCounterOpen(true);
  }
  const { data } = useGetCounterByIdQuery(userInfo?.counter_id || 0);
  const { data: departmentData, refetch } = useGetDepartmentByIdQuery(
    userInfo?.department_id || 0
  );

  useEffect(() => {
    if (data) {
      const isOccupied = data.is_occupied;
      const occupiedBy = data?.occupied_by;
      const currentUser = userInfo?.id;
      const accrossDepartment =
        departmentData?.ticket_rerouting === ReRouteType.ACROSS_DEPT;

      // Counter should be open only if occupiedBy matches current user
      const shouldOpen = isOccupied && occupiedBy === currentUser;

      setIsCounterOpen(shouldOpen);
      localStorage.setItem("counter_open", shouldOpen?.toString());
      console.log("isAccrossDepartment", accrossDepartment);
      console.log("Accress depart", departmentData?.ticket_rerouting);
      setIsAccrossDepartment(accrossDepartment);
    }
  }, [data, userInfo?.id, departmentData]);

  useEffect(() => {
    const fetchCheckedInTicket = async () => {
      try {
        const { data } = await refetchWaitingList();
        const checkedInTicket = data?.counters?.[0]?.tickets?.find(
          (ticket: Ticket) => ticket.status === TicketStatus.CHECKED_IN
        );

        if (checkedInTicket) {
          setCurrentTicket(checkedInTicket);
          setUserId(checkedInTicket.user_id);
          // Only fetch comments if we have a ticket
          await fetchComments(String(checkedInTicket.number));
        }
      } catch (err) {
        console.error("Failed to fetch checked-in ticket on mount:", err);
      }
    };

    fetchCheckedInTicket();
  }, []); // Runs once on component mount

  async function setAccrossDepartment() {
    const result = await refetch();
    const updatedData = result.data;
    const accrossDepartment =
      updatedData?.ticket_rerouting === ReRouteType.ACROSS_DEPT;
    setIsAccrossDepartment(accrossDepartment);
    return accrossDepartment; // Optionally return the value
  }

  const handleTicketAction = useCallback(
    async (
      action: TicketAction,
      setSelectedAction: React.Dispatch<
        React.SetStateAction<TicketAction | null>
      >
    ) => {
      if (!currentTicket) return;

      if (String(currentTicket.user_id) === "0") {
        toast({
          variant: "destructive",
          title: "Cannot move a ticket before adding user details",
          description: "Please add user details before proceeding.",
        });
        setSelectedAction(null);
        return;
      }

      // if (currentTicket.comments.length === 0) {
      //   toast({
      //     variant: "destructive",
      //     title: "Cannot move a ticket without comments",
      //     description: "Please add atleaset one comment before proceeding.",
      //   });
      //   setSelectedAction(null);
      //   return;
      // }

      try {
        setLoading(true);
        setError(null);

        // Update the current ticket's status based on the provided action
        await updateStatus({
          ticket_id: Number(currentTicket.id),
          action: action,
          staff_id: String(userInfo?.id ?? 0),
          trigger_lambda: action === TicketAction.COMPLETE, // Trigger lambda only for COMPLETE
        }).unwrap();

        // Refetch the waiting list to get the updated list of tickets
        const { data } = await refetchWaitingList();
        const pendingTickets = data?.counters?.[0]?.tickets?.filter(
          (ticket: Ticket) =>
            ticket.status !== TicketStatus.HOLD &&
            ticket.status !== TicketStatus.NO_SHOW &&
            ticket.number.startsWith("S-")
        );

        const next = pendingTickets?.[0];
        if (next) {
          readTicket(
            `Ticket Number ${next.number}, please visit counter number ${userInfo?.counter_id}`
          );
          // Mark the next ticket as "SERVING"
          await updateStatus({
            ticket_id: Number(next.id),
            action: TicketAction.CHECKED_IN,
            staff_id: String(userInfo?.id ?? 0),
            trigger_lambda: false,
          }).unwrap();

          // Fetch the latest details for the next ticket
          const queryParams = new URLSearchParams({
            department_id: userInfo?.department_id?.toString() ?? "0",
            counter_id: userInfo?.counter_id?.toString() ?? "0",
            date_filter: localDate,
            sort_by: "created_at",
            sort_order: "asc",
            ticket_number: next.number,
          });
          const query = queryParams.toString();
          const updatedResponse = await triggerGetTickeById({ query }).unwrap();
          const updatedTicket = updatedResponse?.counters?.[0]?.tickets?.[0];

          if (updatedTicket) {
            // Set the next ticket as the current ticket
            setCurrentTicket(updatedTicket);
            setUserId(next.user_id);
            refetch();
          } else {
            setError("Failed to retrieve updated ticket data for next ticket");
          }
        } else {
          // No more tickets in the queue
          setUserId(null);
          setCurrentTicket(null);
        }
        // Reset the selected action after successful completion
        setSelectedAction(null);
      } catch (err) {
        setError(`Failed to execute action: ${action}`);
        // Reset the selected action after successful completion
        setSelectedAction(null);
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, refetchWaitingList, updateStatus, userInfo]
  );

  const nextTicket = useCallback(async () => {
    if (!currentTicket) return;

    try {
      setLoading(true);

      // Mark current ticket as COMPLETE
      await updateStatus({
        ticket_id: Number(currentTicket.id),
        action: TicketAction.COMPLETE,
        staff_id: String(userInfo?.id ?? 0),
        trigger_lambda: true,
      }).unwrap();

      // Refetch the waiting list to get the updated list
      const { data } = await refetchWaitingList();

      const pendingTickets = data?.counters?.[0]?.tickets?.filter(
        (ticket: Ticket) =>
          ticket.status !== TicketStatus.HOLD &&
          ticket.status !== TicketStatus.NO_SHOW &&
          ticket.number.startsWith("S-")
      );

      const next = pendingTickets?.[0];
      if (next) {
        // Mark the next ticket as "SERVING"
        await updateStatus({
          ticket_id: Number(next.id),
          action: TicketAction.CHECKED_IN,
          staff_id: String(userInfo?.id ?? 0),
          trigger_lambda: false,
        }).unwrap();

        // Second API call: Fetch the latest details for the next ticket
        const queryParams = new URLSearchParams({
          department_id: userInfo?.department_id?.toString() ?? "0",
          counter_id: userInfo?.counter_id?.toString() ?? "0",
          date_filter: localDate,
          sort_by: "created_at",
          sort_order: "asc",
          ticket_number: next.number, // Correct ticket reference
        });

        const query = queryParams.toString();
        const updatedResponse = await triggerGetTickeById({ query }).unwrap();

        const updatedTicket = updatedResponse?.counters?.[0]?.tickets?.[0];
        if (updatedTicket) {
          // Set the next ticket as the current ticket
          setCurrentTicket(updatedTicket);
          setUserId(next.user_id);
        } else {
          setError("Failed to retrieve updated ticket data for next ticket");
        }
      } else {
        // No more tickets in the queue
        setUserId(null);
        setCurrentTicket(null);
      }
    } catch (err) {
      setError("Failed to update ticket status");
    } finally {
      setLoading(false);
    }
  }, [currentTicket, refetchWaitingList, updateStatus, userInfo]);

  const holdTicket = useCallback(async () => {
    if (!currentTicket) return;

    try {
      setLoading(true);
      // Mark current ticket as on hold
      await updateStatus({
        ticket_id: Number(currentTicket.id),
        action: TicketAction.HOLD,
        staff_id: String(userInfo?.id ?? 0),
        trigger_lambda: false,
      }).unwrap();

      // Refetch the waiting list to get the next pending ticket
      const { data } = await refetchWaitingList();

      const pendingTickets = data?.counters?.[0]?.tickets?.filter(
        (ticket: Ticket) =>
          ticket.status !== TicketStatus.HOLD &&
          ticket.status !== TicketStatus.NO_SHOW &&
          ticket.number.startsWith("S-")
      );
      const next = pendingTickets?.[0];
      if (next) {
        await updateStatus({
          ticket_id: Number(next.id),
          action: TicketAction.CHECKED_IN,
          staff_id: String(userInfo?.id ?? 0),
          trigger_lambda: false,
        }).unwrap();

        // Set the next ticket as the current ticket
        setCurrentTicket(next);
        setUserId(next.user_id);
      } else {
        setUserId(null);
        // No more tickets in the queue
        setCurrentTicket(null);
      }
    } catch (err) {
      setError("Failed to put ticket on hold");
    } finally {
      setLoading(false);
    }
  }, [currentTicket, refetchWaitingList, updateStatus, staffId]);

  const marketAbsent = useCallback(async () => {
    if (!currentTicket) return;

    try {
      setLoading(true);
      // Mark current ticket as absent
      await updateStatus({
        ticket_id: Number(currentTicket.id),
        action: TicketAction.ABSENT,
        staff_id: String(userInfo?.id ?? 0),
        trigger_lambda: false,
      }).unwrap();
      // Refetch the waiting list to get the next pending ticket
      const { data } = await refetchWaitingList();

      const pendingTickets = data?.counters?.[0]?.tickets?.filter(
        (ticket: Ticket) =>
          ticket.status !== TicketStatus.HOLD &&
          ticket.status !== TicketStatus.NO_SHOW &&
          ticket.number.startsWith("S-")
      );
      const next = pendingTickets?.[0];
      if (next) {
        await updateStatus({
          ticket_id: Number(next.id),
          action: TicketAction.CHECKED_IN,
          staff_id: String(userInfo?.id ?? 0),
          trigger_lambda: false,
        }).unwrap();

        // Set the next ticket as the current ticket
        setCurrentTicket(next);
        setUserId(next.user_id);
      } else {
        // No more tickets in the queue
        setUserId(null);
        setCurrentTicket(null);
      }
    } catch (err) {
      setError("Failed to mark ticket as absent");
    } finally {
      setLoading(false);
    }
  }, [currentTicket, nextTicket, updateStatus, staffId]);

  const fetchComments = useCallback(
    async (ticketId: string) => {
      try {
        const queryParams = new URLSearchParams({
          department_id: userInfo?.department_id?.toString() ?? "0",
          counter_id: userInfo?.counter_id?.toString() ?? "0",
          date_filter: localDate,
          sort_by: "created_at",
          sort_order: "asc",
          ticket_number: ticketId,
          skip: "0",
          limit: "100",
        });

        const query = queryParams.toString();

        await triggerGetTickeById({ query: query })
          .unwrap()
          .then((response) => {
            const newComments =
              response?.counters[0]?.tickets[0]?.comments ?? [];

            setComments((prevComments) => newComments);
            setCurrentTicket(response?.counters[0]?.tickets[0]);
            setUserId(response?.counters[0]?.tickets[0]?.user_id ?? null);
            refetch();
          });
        // Check if new comments are different before updating state
      } catch (err) {
        setError("Failed to fetch comments");
      }
    },
    [currentTicket, nextTicket, updateStatus, staffId]
  );

  const addComment = useCallback(
    async (ticketId: number, comment: string) => {
      if (!currentTicket) return;
      try {
        setLoading(true);
        await updateTicket({
          ticket_id: ticketId,
          action: TicketAction.COMMENT,
          staff_id: String(userInfo?.id ?? 0), // Replace with actual staff ID
          comment_action: {
            action: CommentAction.ADD,
            comment: comment,
          },
        }).unwrap();
        await fetchComments(String(currentTicket.number));
      } catch (err) {
        setError("Failed to add comment");
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, nextTicket, updateStatus, staffId]
  );

  const updateComment = useCallback(
    async (ticketId: number, commentId: number, newComment: string) => {
      if (!currentTicket) return;
      try {
        setLoading(true);
        await updateTicket({
          ticket_id: ticketId,
          action: TicketAction.COMMENT,
          staff_id: String(userInfo?.id ?? 0), // Replace with actual staff ID
          comment_action: {
            action: CommentAction.UPDATE,
            comment: newComment,
            comment_id: commentId,
          },
        }).unwrap();
        await fetchComments(String(currentTicket.number));
      } catch (err) {
        setError("Failed to add comment");
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, nextTicket, updateStatus, staffId]
  );

  const deleteComment = useCallback(
    async (ticketId: number, commentId: number) => {
      if (!currentTicket) return;
      try {
        setLoading(true);

        const response = await updateTicket({
          ticket_id: ticketId,
          action: TicketAction.COMMENT,
          staff_id: String(userInfo?.id ?? 0), // Replace with actual staff ID
          comment_action: {
            action: CommentAction.DELETE,
            comment_id: commentId,
          },
        }).unwrap();
        await fetchComments(String(currentTicket.number));
      } catch (err) {
        setError("Failed to add comment");
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, nextTicket, updateStatus, staffId]
  );
  const addDetails = useCallback(
    async (data: {
      title: string;
      description?: string;
    }): Promise<{ success: boolean; message: string }> => {
      if (!currentTicket) {
        return { success: false, message: "No current ticket available" };
      }
      try {
        setLoading(true);

        await updateTitle({
          action: TicketAction.UPDATE_DETAILS,
          ticketId: Number(currentTicket.id),
          staff_id: String(userInfo?.id ?? 0),
          ticketData: {
            title: data.title,
            description: data.description ?? "",
          },
        }).unwrap();

        await fetchComments(String(currentTicket.number));

        return { success: true, message: "Details updated successfully" };
      } catch (err) {
        setError("Failed to update details");
        return { success: false, message: "Failed to update details" };
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, fetchComments, updateTitle, userInfo]
  );

  const updateLinkedTicket = useCallback(
    async (data: {
      ticketId: string;
    }): Promise<{ success: boolean; message: string }> => {
      if (!currentTicket) {
        return { success: false, message: "No current ticket available" };
      }
      try {
        setLoading(true);
        await addLinkedTicket({
          action: TicketAction.LINK,
          ticket_id: Number(currentTicket.id),
          staff_id: String(userInfo?.id ?? 0),
          parentTicketId: Number(data.ticketId),
          trigger_lambda: false,
        }).unwrap();
        await fetchComments(String(currentTicket.number));
        return { success: true, message: "Details updated successfully" };
      } catch (err) {
        setError("Failed to update details");
        return { success: false, message: "Failed to update details" };
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, fetchComments, updateTitle, userInfo]
  );

  const routeTicket = useCallback(
    async (counterId: number, serviceId: number): Promise<{
      success: boolean;
      message: string;
    }> => {
      if (!currentTicket) {
        return {
          success: false,
          message: "No active ticket to route",
        };
      }

      try {
        setLoading(true);

        const updateRequest = {
          ticket_id: Number(currentTicket.id),
          action: TicketAction.ROUTE,
          staff_id: String(userInfo?.id ?? 0),
          trigger_lambda: false,
        };

        if (Number(counterId) > 0) {
          Object.assign(updateRequest, { counter_id: counterId });
        }

        if (Number(serviceId) > 0) {
          Object.assign(updateRequest, { service_id: serviceId });
        }

        await updateStatus(updateRequest).unwrap();

        // Refetch waiting list for next ticket
        const { data } = await refetchWaitingList();

        const pendingTickets = data?.counters?.[0]?.tickets?.filter(
          (ticket: Ticket) =>
            ticket.status !== TicketStatus.HOLD &&
            ticket.status !== TicketStatus.NO_SHOW &&
            ticket.number.startsWith("S-")
        );

        // Handle next ticket if available
        const next = pendingTickets?.[0];
        if (next) {
          await updateStatus({
            ticket_id: Number(next.id),
            action: TicketAction.CHECKED_IN,
            staff_id: String(userInfo?.id ?? 0),
            trigger_lambda: false,
          }).unwrap();

          setCurrentTicket(next);
          setUserId(next.user_id);
        } else {
          setUserId(null);
          setCurrentTicket(null);
        }

        return {
          success: true,
          message: `Ticket successfully routed`,
        };

      } catch (err) {
        const typedError = err as ErrorType;
        const errorMessage = typedError.data?.detail || typedError.data?.message || "Failed to route ticket";

        return {
          success: false,
          message: errorMessage
        };
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, updateStatus, userInfo?.id, refetchWaitingList]
  );

  const generatingTicketRef = useRef(false);

  const convertTicketFromRToS = async (
    ticketNumber: string,
    service_id: number,
    r_ticket_id: number,
    title: string,
    userId?: number,
    description?: string,
    options?: { throwOnError?: boolean }
  ) => {
    if (ticketNumber.startsWith("S-")) return;
    if (generatingTicketRef.current) return;

    setGeneratingTicket(true); // Prevent multiple generations

    generatingTicketRef.current = true;

    const hours = today.getHours().toString().padStart(2, "0");
    const minutes = today.getMinutes().toString().padStart(2, "0");
    const seconds = today.getSeconds().toString().padStart(2, "0");
    const localTime = `${hours}:${minutes}:${seconds}`;
    try {
      const generatedTicket = await generateTicket({
        title: title,
        description: description ?? "",
        date: localDate,
        time: `${hours}:${minutes}:${seconds}`,
        service_id: service_id,
        user_id: userId ?? 0, // Provide a default value of 0 if userId is undefined
        eta: 0,
        r_ticket_id: r_ticket_id,
      }).unwrap();

      if (!generatedTicket?.ticket_number) {
        setError("Failed to generate ticket");
        return;
      }

      return generatedTicket;
    } catch (error) {
      const typedError = error as ErrorType;
      const errorMessage = typedError.data?.detail || typedError.data?.message || "Failed to generate ticket";
      setError("Error generating ticket");
      if (options?.throwOnError) {
        throw typedError; // propagate full error to caller
      }
      return undefined; // fallback: signal failure without throwing
    } finally {
      generatingTicketRef.current = false; // Reset flag after completion
      setGeneratingTicket(false); // Reset flag after completion
    }
  }

  const selectTicket = useCallback(
    async (
      ticketNumber: string,
      service_id: number,
      r_ticket_id: number,
      title: string,
      userId?: number,
      description?: string
    ) => {
      let finalTicketNumber = ticketNumber;

      if (!ticketNumber.startsWith("S-")) {
        const generatedTicket = await convertTicketFromRToS(
          ticketNumber,
          service_id,
          r_ticket_id,
          title,
          userId,
          description,
        )
        finalTicketNumber = generatedTicket?.ticket_number
      }

      try {
        if (currentTicket && currentTicket.number === ticketNumber) {
          //  This ticket is already selected
          return;
        }

        setLoading(true);
        setError(null);

        if (currentTicket) {
          await updateStatus({
            ticket_id: Number(currentTicket.id),
            action: TicketAction.HOLD,
            staff_id: String(userInfo?.id ?? 0),
            trigger_lambda: false,
          }).unwrap();
        } else {
          // No current ticket to hold
        }

        const queryParams = new URLSearchParams({
          department_id: userInfo?.department_id?.toString() ?? "0",
          counter_id: userInfo?.counter_id?.toString() ?? "0",
          date_filter: localDate,
          sort_by: "created_at",
          sort_order: "asc",
          ticket_number: finalTicketNumber,
        });

        const response = await triggerGetTickeById({
          query: queryParams.toString(),
        }).unwrap();
        let selectedTicket = response?.counters?.[0]?.tickets?.[0];

        if (!selectedTicket) {
          setError("Ticket not found");
          return;
        }

        if (selectedTicket.status !== TicketStatus.CHECKED_IN) {
          await updateStatus({
            ticket_id: Number(selectedTicket.id),
            action: TicketAction.CHECKED_IN,
            staff_id: String(userInfo?.id ?? "0"),
            trigger_lambda: false,
          }).unwrap();
        }

        const updatedQuery = new URLSearchParams({
          department_id: userInfo?.department_id?.toString() ?? "0",
          counter_id: userInfo?.counter_id?.toString() ?? "0",
          date_filter: localDate,
          sort_by: "created_at",
          sort_order: "asc",
          ticket_number: finalTicketNumber,
        });

        const updatedResponse = await triggerGetTickeById({
          query: updatedQuery.toString(),
        }).unwrap();

        selectedTicket = updatedResponse?.counters?.[0]?.tickets?.[0];

        if (!selectedTicket) {
          setError("Failed to retrieve updated ticket data");
          return;
        }

        setCurrentTicket(selectedTicket);
        setUserId(selectedTicket.user_id);
      } catch (err) {
        setError("Failed to select and serve ticket");
      } finally {
        setLoading(false);
      }
    },
    [
      triggerGetTickeById,
      updateStatus,
      userInfo,
      currentTicket,
      generatingTicket,
    ]
  );

  const startServing = useCallback(async () => {
    try {
      setLoading(true);
      // Refetch the waiting list
      const { data } = await refetchWaitingList();

      // First check if there's already a ticket with CHECKED_IN status
      const currentlyServingTickets = data?.counters?.[0]?.tickets?.filter(
        (ticket: Ticket) =>
          ticket.status === TicketStatus.CHECKED_IN &&
          ticket.number.startsWith("S-")
      );

      // If a ticket is already being served, use that one
      if ((currentlyServingTickets ?? []).length > 0) {
        const servingTicket = currentlyServingTickets![0]; // Use non-null assertion as we've already checked length
        setCurrentTicket(servingTicket);
        setUserId(servingTicket.user_id);
        refetch();
        return;
      }

      // Otherwise, find the first available ticket that's not being served
      const pendingTickets = data?.counters?.[0]?.tickets?.filter(
        (ticket: Ticket) =>
          ticket.status !== TicketStatus.HOLD &&
          ticket.status !== TicketStatus.NO_SHOW &&
          ticket.number.startsWith("S-")
      );
      const firstTicket = pendingTickets?.[0];

      if (!firstTicket) {
        toast({
          variant: "destructive",
          title: "No Ticket in Queue To Serve.",
        });
        return;
      }

      // Optimistically update UI before API call to reduce flicker
      setCurrentTicket((prev) => prev || firstTicket);
      setUserId((prev) => prev || firstTicket.user_id);

      // Update the ticket status to "serving"
      await updateStatus({
        ticket_id: Number(firstTicket.id),
        action: TicketAction.CHECKED_IN,
        staff_id: staffId,
        trigger_lambda: false,
      }).unwrap();

      const queryParams = new URLSearchParams({
        department_id: userInfo?.department_id?.toString() ?? "0",
        counter_id: userInfo?.counter_id?.toString() ?? "0",
        date_filter: localDate,
        sort_by: "created_at",
        sort_order: "asc",
        ticket_number: firstTicket.number,
      });

      const query = queryParams.toString();
      const response = await triggerGetTickeById({ query }).unwrap();

      const selectedTicket = response?.counters?.[0]?.tickets?.[0];

      // Only update if the new ticket is different
      if (selectedTicket) {
        //on serve sound needed
        setCurrentTicket(selectedTicket);
        readTicket(
          `Ticket Number ${selectedTicket.number}, please visit counter number ${userInfo?.counter_id}`
        );
        setUserId(selectedTicket.user_id);
        refetch();
      }
    } catch (err) {
      setError("Failed to start serving ticket");
    } finally {
      setLoading(false);
    }
  }, [
    refetchWaitingList,
    updateStatus,
    staffId,
    userInfo,
    localDate,
    triggerGetTickeById,
    refetch,
  ]);

  const addUser = useCallback(
    async (userData: UserData) => {
      try {
        setLoading(true);

        // Send the user data to your API service to add the user
        const resp = await addUserAgainstTicket({
          ticket_id: Number(currentTicket?.id),
          staff_id: staffId,
          action: TicketAction.UPDATE_USER,
          user_data: userData,
        }).unwrap();
        // After adding the user, refetch the ticket data by ticket number
        if (currentTicket) {
          await fetchComments(String(currentTicket.number)); // Ensure to fetch comments or other ticket data again
        }

        return { success: true, message: "User Details updated successfully" }; // Return success response
      } catch (err) {
        const errorMessage =
          (err as any).data?.message ||
          (err as any).data?.detail ||
          (err as any).message ||
          "An error occurred";
        setError(errorMessage);

        // Display toast notification for error
        console.error("Error", { error: err });
        if (err && typeof err === "object" && "data" in err) {
          const typedError = err as ErrorType;
          toast({
            variant: "destructive",
            title: "Error in form Submission",
            description: `${typedError.data?.detail ?? typedError.data?.message}`,
          });
        }
        return { success: false, message: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [currentTicket, fetchComments] // Dependencies
  );
  return (
    <TicketContext.Provider
      value={{
        handleTicketAction,
        currentTicket,
        nextTicket,
        holdTicket,
        marketAbsent,
        loading,
        error,
        userId,
        setUserId,
        addComment,
        updateComment,
        deleteComment,
        routeTicket,
        selectTicket,
        startServing,
        comments: comment,
        fetchComments,
        addUser,
        addDetails,
        setCounterOpen,
        isCounterOpen,
        updateLinkedTicket,
        isAccrossDepartment,
        setAccrossDepartment,
        convertTicketFromRToS
      }}
    >
      {children}
    </TicketContext.Provider>
  );
}

export const useTicket = () => useContext(TicketContext);
